import { Component, OnInit, ViewChild } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { HttpClient, HttpHeaders, HttpParams, HttpStatusCode } from '@angular/common/http';
import { Subject, firstValueFrom } from 'rxjs';

import { DashboardService } from '../../../../../service/events/dashboard.service';
import { FincasApiService } from '../../../../../service/api/fincas-api.service';
import { ClientesApiService } from '../../../../../service/api/clientes-api.service';
import { ProductosApiService } from '../../../../../service/api/productos-api';

import { BaseForm } from '../../../../base-form';
import { StorageManager } from '../../../../../common/storage-manager.class';
import { FormRequestTypes } from '../../../../../common/classes/form-request';
import { environment } from '../../../../../../environments/environment';
import { build, buildParams, pool } from '../../../../../common/classes/request-builder';

import { FincasEnvironmentDefinition } from '../environment-variables';
import { FincasFormDefinition } from './form-definition';
import { ReactiveFormBuilderComponent } from '../../../../../common/components/reactive-form-builder/form-builder.component';

import { FincasModel } from '../../../../../models/form-common/fincas.module';
import { EventPhase, FormPhase } from '../../../../../common/components/reactive-form-builder/form-request';
import { ClienteModel } from '../../../../../models/cliente.model';

const FINCA_NAME = environment.features.fincaName as string;

/** Message state for REA dialog notifications */
type MessageState = 'warning' | 'error' | 'success';

/** Endpoints relative to serverUrl (single source of truth) */
const API = {
  EXPORT_REA: 'ws/index.php?p1=fincas&p2=exportarREA',
  GET_LAST_REGISTER_REA: 'ws/index.php?p1=fincas&p2=getLastRegisterREA',
} as const;

/** Literal tokens used in list builds */
const LIST_TOKENS = {
  PRODUCT_TYPE: 'fertilizante',
  LABEL_KEY: 'nombre',
} as const;

/** REA spinner visual tokens */
const SPINNER = {
  STROKE: '6',
  DURATION: '1.5s',
} as const;

/** Minimal typing for server responses */
interface ReaImportData {
  parcelasNoInsertadas?: number;
  parcelasInsertadas?: number;
  parcelasNoActualizadas?: number;
  parcelasActualizadas?: number;
  errores?: Array<{ descripcion: string }>;
}

interface ReaImportResult {
  statusCode: number;
  statusMessage?: string;
  data?: ReaImportData;
}

interface ReaHistory {
  statusCode: number;
  data?: { fecha?: string };
}

@Component({
  selector: 'app-finca-form',
  standalone: false,
  templateUrl: './finca-form.component.html',
  styleUrls: ['./finca-form.component.scss'],
})
export class FincaFormComponent extends BaseForm<FincasModel> implements OnInit {
  @ViewChild(ReactiveFormBuilderComponent)
  public formBuilder!: ReactiveFormBuilderComponent<FincasModel>;

  /** Reactive form definition bound to the template */
  public form!: FincasFormDefinition;

  /** Form request engine (phase-based hooks) */
  public readonly formRequest!: ReactiveFormBuilderComponent<FincasModel>;

  /** Environment flags for feature toggles and labels */
  public readonly env: FincasEnvironmentDefinition = new FincasEnvironmentDefinition();

  /** Emits when dropdowns are ready to be reloaded */
  public readonly dropdownLoadTrigger = new Subject<void>();

  /** Dialog breakpoints (no magic numbers in template) */
  public readonly REA_DIALOG_BREAKPOINTS: Readonly<Record<string, string>> = {
    '1600px': '48vw',
    '1199px': '65vw',
    '768px': '85vw',
    '480px': '95vw',
  };

  /** Spinner presentation tokens */
  public readonly REA_SPINNER_STROKE = SPINNER.STROKE;

  public readonly REA_SPINNER_DURATION = SPINNER.DURATION;

  /** Expose enums/tokens to the template */
  public readonly HTTP = HttpStatusCode;

  public readonly FormRequestTypes = FormRequestTypes;

  // UI state
  public showDialogExportREA = false;
  
  public showSpinner = false;
  
  public showBtnConfirm = true;
  
  public messageState: MessageState | '' = '';

  // Data
  public importResult: ReaImportResult | null = null;
  
  public historicoREA: ReaHistory | null = null;

  public error = '';

  constructor(
    public override router: Router,
    public override route: ActivatedRoute,
    public override dashboard: DashboardService,
    public fincasApi: FincasApiService,
    public clientesApi: ClientesApiService,
    public productosApi: ProductosApiService,
    private readonly http: HttpClient
  ) {
    super(
      route,
      router,
      dashboard,
      fincasApi.fincasPUT,
      fincasApi.fincasPOST,
      'finca',
      `Editar ${FINCA_NAME}`,
      `Crear ${FINCA_NAME}`,
      `Duplicar ${FINCA_NAME}`
    );
    this.formRequest = new ReactiveFormBuilderComponent(this.http);
  }

  /** Disabled state for REA button (no string/length literals in template) */
  public get isReaDisabled(): boolean {
    const code = this.form?.model?.codigo_REA;

    return (code == null) || String(code).trim().length === 0;
  }

  ngOnInit(): void {
    const type = this.getType();
    const baseModel = this.getModel() as unknown as Record<string, string>;

    this.form = new FincasFormDefinition(type, baseModel, null);

    void this.initFormData();

    if (this.env.fincasNifAutocomplete && type === FormRequestTypes.CREATE) {
      this.form.model.nif = StorageManager.getClient().nif ?? {};
    }

    if (!this.env.activateREA) {
      const loggedInClient = StorageManager.getUser();
      
      this.form.model.id_cliente = loggedInClient.id;
    }

    // Phase: LOAD → fetch lists before rendering dependent fields
    this.formRequest.executePhase(FormPhase.LOAD);
    this.softInit(type);

    this.formRequest.addEvent(FormPhase.LOAD, EventPhase.AFTER, (resolve) => {
      this.getLastRegisterREA();
      resolve(true);
    });
  }

  public formChanges(formModel: object, _tag: string): void {
    this.form.model = formModel as FincasModel;
  }

  /** Show last REA import info for the current finca (edit mode only). */
  public getLastRegisterREA(): void {
    if (!(this.env.activateREA && this.model.codigo_REA != null && this.getType() === FormRequestTypes.EDIT)) {
      return;
    }

    const url = `${environment.serverUrl}/${API.GET_LAST_REGISTER_REA}`;
    const params = new HttpParams().set('codigo_REA', this.model.codigo_REA);

    this.http.get<ReaHistory>(url, { params }).subscribe((res) => {
      this.historicoREA = res;
    });
  }

  /** Click handler: starts REA export with spinner */
  public onExportREA(): void {
    this.showSpinner = true;
    void this.exportREA(this.form.model as FincasModel);
  }

  /** Restore dialog to initial state */
  public resetReaDialog(): void {
    this.showDialogExportREA = false;
    this.showSpinner = false;
    this.messageState = '';
    this.showBtnConfirm = true;
    this.importResult = null;
  }

  /** Export REA for current finca; updates dialog state with the response */
  public async exportREA(data: FincasModel): Promise<void> {
    const ready = !!((data.codigo_REA != null) && (data.nif != null));
    
    if (!ready) {
      this.messageState = 'warning';
      this.showSpinner = false;
      return;
    }

    const url = `${environment.serverUrl}/${API.EXPORT_REA}`;

    const bodyParams = new HttpParams({
      fromObject: {
        id_finca: String(data.id ?? ''),
        nombre_finca: String(data.nombre ?? ''),
        id_usuario: String(StorageManager.getUser().id ?? ''),
        id_cliente: String(data.id_cliente?.id ?? ''),
        codigo_REA: String(data.codigo_REA ?? ''),
        nif: String(data.nif ?? ''),
        tipoFormulario: String(this.getType()),
      },
    });

    const headers = new HttpHeaders({ 'Content-Type': 'application/x-www-form-urlencoded' });

    try {
      const res = await firstValueFrom(
        this.http.post<ReaImportResult>(url, bodyParams.toString(), { headers })
      );

      switch (res?.statusCode) {
        case HttpStatusCode.Accepted: // 202
          this.messageState = 'success';
          this.importResult = res;
          this.flag = 'edit';
          this.request = this.putRequest;
          break;

        case HttpStatusCode.UnprocessableEntity: // 422
          this.messageState = 'error';
          this.importResult = {
            ...res,
            statusMessage: res?.data?.errores?.[0]?.descripcion ?? 'Validación incorrecta',
          };
          break;

        case HttpStatusCode.InternalServerError: // 500
          this.messageState = 'error';
          this.importResult = res;
          break;

        default:
          this.messageState = 'error';
          this.importResult = { statusCode: 0, statusMessage: 'Respuesta desconocida del servidor' };
      }
    } catch (err) {
      this.messageState = 'error';
      this.importResult = { statusCode: 0, statusMessage: 'Error al importar REA', data: [] as ReaImportData };
      console.error('💥 Error al importar REA => ', err);
    } finally {
      this.showSpinner = false;
      this.showBtnConfirm = false;
    }
  }

  /** Build dropdown data for the form and notify reactive-form-builder. */
  /**
   * Build dropdown data for the form and notify the reactive form-builder.
   * Triggers `dropdownLoadTrigger` even when there are no build tasks.
   */
  private initFormData(): Promise<boolean> {
    type BuildTask = (resolve: (ok: boolean) => void) => void;

    const builds: BuildTask[] = [];

    // Collect tasks conditionally
    if (this.env.hasClients) {
      builds.push(
        build(this.clientesApi.clientesGET, this.form.lists.clientes,
        (it: ClienteModel) => `${it.nombre  } ${  it.apellidos || ''  } ${it.apellido_1 || '' } ${it.apellido_2 || '' }`,
        false),
      );
    }

    if (this.env.showFincasProductos) {
      builds.push(
        buildParams(
          this.productosApi.productos.GET,
          { tipo: LIST_TOKENS.PRODUCT_TYPE },
          this.form.lists.productos,
          LIST_TOKENS.LABEL_KEY,
          false,
          true
        )
      );
    }

    // Common notifier: run in a microtask to mirror async pool completion timing
    const notify = (): void => { queueMicrotask(() => { this.dropdownLoadTrigger.next(); }); };

    // No tasks -> fire trigger efficiently and resolve
    if (builds.length === 0) {
      notify();
      return Promise.resolve(true);
    }

    // Tasks exist -> fire trigger after pool resolves
    return pool(builds, notify);
  }

}
